
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = "https://pmmrezxcyemynydfiatc.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBtbXJlenhjeWVteW55ZGZpYXRjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDAzNTgyMDksImV4cCI6MjA1NTkzNDIwOX0.KX_DEimtCzC7UfQLZTAH3L3aWFB-zAQTlS6MKpC_bec";

// Create the Supabase client with persistSession set to false
export const supabase = createClient<Database>(
  SUPABASE_URL, 
  SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      persistSession: false, // Disable persistent sessions to require login on refresh
      autoRefreshToken: false, // Also disable auto refresh to prevent token issues
    }
  }
);

// Helper function to determine the correct redirect URL
export const getRedirectUrl = () => {
  // Use the exact current origin for more reliable redirect
  const redirectUrl = window.location.origin;
  
  console.log("Using redirect URL:", redirectUrl);
  return redirectUrl;
};

// Helper function to get the user profile from auth
export const getUserProfile = async () => {
  try {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !sessionData.session) {
      console.warn("No active session found:", sessionError);
      return null;
    }
    
    // Get user profile from profiles table
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', sessionData.session.user.id)
      .single();
      
    if (profileError) {
      console.error("Error fetching profile:", profileError);
      
      // If profile doesn't exist yet, try to force create it
      if (profileError.code === 'PGRST116') {
        console.log("Profile not found, creating one...");
        await forceRefreshUserProfile(sessionData.session.user.id);
        
        // Try fetching again after creating
        const { data: newProfileData, error: newProfileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', sessionData.session.user.id)
          .single();
          
        if (newProfileError) {
          console.error("Error fetching newly created profile:", newProfileError);
          return null;
        }
        
        return newProfileData;
      }
      
      return null;
    }
    
    console.log("Found user profile:", profileData);
    return profileData;
  } catch (err) {
    console.error("Error in getUserProfile:", err);
    return null;
  }
};

// Helper function to force refresh/create a user profile
export const forceRefreshUserProfile = async (authUserId: string) => {
  try {
    console.log("Force refreshing profile for user:", authUserId);
    
    // First try the RPC method which now handles cleaning usernames properly
    const { data, error } = await supabase.rpc('force_refresh_user_profile', {
      p_user_id: authUserId
    });
    
    if (error) {
      console.error("Error refreshing user profile via RPC:", error);
      
      // If RPC fails, try direct upsert
      console.log("Trying direct profile upsert as fallback...");
      
      // Get user data from auth
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError || !userData.user) {
        console.error("Error getting user data for profile creation:", userError);
        return false;
      }
      
      // Extract profile data from user metadata
      const metadata = userData.user.user_metadata || {};
      console.log("User metadata for profile creation:", metadata);
      
      // Extract Discord ID from metadata
      const discordId = metadata.sub || metadata.id || authUserId;
      
      // Extract the raw username from metadata
      let rawUsername = metadata.preferred_username || 
                      metadata.username || 
                      metadata.name || 
                      metadata.full_name || 
                      metadata.email || 
                      metadata.global_name ||
                      metadata.display_name ||
                      `user_${Math.random().toString(36).substring(2, 10)}`;
      
      // Remove Discord discriminator if present (the #XXXX part)
      const username = rawUsername.replace(/#\d+$/, '');
      
      const avatarUrl = metadata.avatar_url || metadata.avatar || metadata.picture;
      
      console.log("Original username with possible discriminator:", rawUsername);
      console.log("Cleaned username without discriminator:", username);
      console.log("Using consistent username for both profile and user:", username);
      
      // Insert/update profile directly
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: authUserId,
          username,
          avatar_url: avatarUrl,
          updated_at: new Date().toISOString()
        });
        
      if (profileError) {
        console.error("Error in direct profile creation:", profileError);
        return false;
      }
      
      // First check if user record already exists
      const { data: existingUserData, error: existingUserError } = await supabase
        .from('users')
        .select('id, username')
        .eq('discord_id', discordId)
        .single();
        
      if (existingUserError && existingUserError.code !== 'PGRST116') {
        // If error is not "no rows returned", log and return
        console.error("Error checking existing user:", existingUserError);
        return false;
      }
      
      if (existingUserData) {
        // If user exists but username doesn't match what we have now, update it
        if (existingUserData.username !== username) {
          console.log("Updating existing user record to match profile username");
          const { error: updateError } = await supabase
            .from('users')
            .update({ username })
            .eq('discord_id', discordId);
            
          if (updateError) {
            console.error("Error updating user record:", updateError);
            return false;
          }
        } else {
          console.log("User record exists with matching username, no update needed");
        }
      } else {
        // If user record doesn't exist, create it
        console.log("Creating new user record with username:", username);
        const { error: userRecordError } = await supabase
          .from('users')
          .insert({
            discord_id: discordId,
            username
          });
          
        if (userRecordError) {
          console.error("Error creating user record:", userRecordError);
          return false;
        }
      }
      
      console.log("Profile refresh successful (via direct upsert)");
      return true;
    }
    
    console.log("Profile refresh successful (via RPC)");
    return true;
  } catch (err) {
    console.error("Error in forceRefreshUserProfile:", err);
    return false;
  }
};

// Helper function to ensure a user record exists for the authenticated user
export const ensureUserExists = async (authUserId: string) => {
  try {
    // First check if user already exists in the mapping
    const existingUserId = await getUserIdForRecommendation(authUserId);
    if (existingUserId) {
      console.log("User already exists with ID:", existingUserId);
      return existingUserId;
    }

    // Try to force create/refresh the profile
    const profileRefreshed = await forceRefreshUserProfile(authUserId);
    if (!profileRefreshed) {
      console.error("Failed to refresh/create profile");
      return null;
    }
    
    // Get the Discord ID from auth user metadata
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) {
      console.error("Error getting user data:", userError);
      return null;
    }
    
    const metadata = userData.user?.user_metadata || {};
    const discordId = metadata.sub || metadata.id || authUserId;
    
    // Get the user ID using the Discord ID
    const { data: userIdData, error: userIdError } = await supabase
      .from('users')
      .select('id')
      .eq('discord_id', discordId)
      .single();
      
    if (userIdError) {
      console.error("Error getting user ID from Discord ID:", userIdError);
      
      // As a fallback, try getting the user ID based on username
      console.log("Trying fallback to get user ID via username");
      
      // Get username from profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', authUserId)
        .single();
        
      if (profileError) {
        console.error("Error getting profile for fallback:", profileError);
        return null;
      }
      
      // Use username to get user ID
      const { data: userByUsernameData, error: userByUsernameError } = await supabase
        .from('users')
        .select('id')
        .eq('username', profileData.username)
        .single();
        
      if (userByUsernameError) {
        console.error("Error getting user by username:", userByUsernameError);
        return null;
      }
      
      console.log("Found user ID via username fallback:", userByUsernameData.id);
      return userByUsernameData.id;
    }
    
    console.log("Found user ID for recommendations:", userIdData.id);
    return userIdData.id;
  } catch (err) {
    console.error("Exception in ensureUserExists:", err);
    return null;
  }
};

// Helper function to get the user ID from the recommendation service
export const getUserIdForRecommendation = async (authUserId: string | undefined) => {
  if (!authUserId) {
    console.log("No auth user ID provided");
    return null;
  }

  try {
    // First, check if we can map the auth ID to a user ID using the RPC
    const { data, error } = await supabase.rpc('get_user_id_from_auth_id', {
      auth_id: authUserId
    });

    if (error) {
      console.error("Error getting user ID from RPC:", error);
      
      // Fallback to direct query if RPC fails
      console.log("Trying direct query to map auth ID to user ID");
      
      // Get username from profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', authUserId)
        .single();
        
      if (profileError) {
        console.error("Error getting profile:", profileError);
        return null;
      }
      
      // Make sure the username isn't null
      if (!profileData.username) {
        console.error("Profile username is null");
        return null;
      }
      
      // Use username to get user ID
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('username', profileData.username)
        .single();
        
      if (userError) {
        console.error("Error getting user from username:", userError);
        return null;
      }
      
      console.log("Mapped auth ID to user ID via username:", userData.id);
      return userData.id;
    }

    console.log("Mapped auth ID to user ID via RPC:", data);
    return data;
  } catch (err) {
    console.error("Exception getting user ID mapping:", err);
    return null;
  }
};
